import React, { useEffect, useState } from 'react'

import styled, { keyframes } from 'styled-components'
import { createGlobalStyle } from 'styled-components'

import Footer from 'src/modules/organisms/footer/Footer'
import { TitleH1, TopHat } from 'src/components/blocks/Typography'
import {
  BasinFormEmbed,
  BasinEventListener
} from 'src/components/partials/BasinFormEmbed'
import { Container } from 'src/components/partials/Container'
import Layout from 'src/components/partials/Layout'
import SEO from 'src/components/partials/Seo'
import { FlatfileLogoSVG } from 'src/components/svg/Icon'
import { captureEvents } from 'src/utilities/analyticsEvents'
import { Breakpoints } from 'src/utilities/Breakpoints'
import { BrandColors, BrandTokens } from 'src/utilities/Colors'
import { FontFamily } from 'src/utilities/Type'

const ReleaseBG = createGlobalStyle`
  html.fall-release {
    background-color: ${BrandTokens.backgroundDark};

    &:before {
      content: '';
      display: block;
      height: 100vh;
      width: 100%;
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      z-index: -1;
      background: linear-gradient(167deg, #202020 0%, #0E0E11 32%);
    }
  }
  .fall-release body {
    /* background-color: ${BrandTokens.backgroundDark}; */
    background-image: url('/img/shared/grid-bg-mobile.webp');
    /* background-size: 100% 100%; */
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center center;

    @media (min-width: ${Breakpoints.md}px) {
      background-image: url('/img/shared/tunnel-bg-5.svg');
    }
  }
`
const NavWrapper = styled.div`
  z-index: 10;
  background-color: transparent;
  position: static;
  ${Container} {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-block: 1rem;
  }
`

const LogoLink = styled.a`
  display: block;
  svg {
    vertical-align: middle;
  }
`

const LandingPageNav = () => (
  <NavWrapper
    style={{
      zIndex: 10,
      position: 'static'
    }}
  >
    <Container>
      <LogoLink href='/'>
        <FlatfileLogoSVG color={BrandColors.white} height='24px' />
      </LogoLink>
      <div> </div>
    </Container>
  </NavWrapper>
)

// Hero container
const HeroWrapper = styled.div`
  min-height: 100%;
  position: relative;
  overflow: hidden;
  color: white;
  /* max-width: 2100px; */
  margin-inline: auto;
  min-height: calc(100vh - 105px);

  .largeGridBg {
    display: none;
  }

  .mediumGridBg {
    display: block;
  }

  @media (min-width: ${Breakpoints.lg}px) and (min-height: 1400px) {
    aspect-ratio: 16/9;
    height: calc(100vh - 105px);
    /* min-height: 800px; */
    display: flex;
    justify-content: center;
    align-items: center;

    .largeGridBg {
      display: block;
    }

    .mediumGridBg {
      display: none;
    }
  }

  @media (min-height: 1400px) and (min-width: ${Breakpoints.lg}px) {
    height: calc(100vh - 105px);
    aspect-ratio: unset !important;
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const HeroContentWrapper = styled.div`
  /* height: 100%; */
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 3rem 1rem 4rem;

  @media (min-width: ${Breakpoints.lg}px) and (min-height: 1400px) {
    /* padding: 6rem 1rem 4rem; */
    padding: 0 1rem;
    margin-top: -8%;
  }

  @media (min-width: 1860px) {
    /* padding: 10rem 1rem 4rem; */
  }

  animation: ${fadeIn} 0.75s ease-in-out;
`

const FormWrapper = styled.div`
  /* max-width: 760px; */
  margin-inline: auto;
  /* padding-block: 6.25rem; */
  padding-inline: 1rem;

  .submitted-message {
    p {
      border: 0;
      padding: 0;
      font-size: 1rem;
      max-width: 50ch;
      margin-inline: auto;
    }
    p span {
      color: ${BrandTokens.textLight} !important;
    }
  }

  input {
    color: ${BrandTokens.textDark};
  }

  .hs-button {
    border: 1px solid ${BrandTokens.textLight} !important;
    background-color: transparent !important;
    color: ${BrandTokens.textLight} !important;
    padding: 0.75rem 1.5rem !important;
    font-weight: 400 !important;
    font-family: ${FontFamily.brandCode} !important;
    font-size: 0.875rem !important;
    margin-inline: auto;

    &:hover,
    &:focus,
    &:active {
      background-color: transparent !important;
      color: ${BrandTokens.textLight} !important;
      border-color: ${BrandTokens.textLight} !important;
    }
  }
`

const HeroSection = () => {
  // Keep track of step
  const [step, setStep] = useState(1)

  const handleFormReady = () => {
    captureEvents({
      posthog: {
        data: {
          source: 'pre-demo-questionnaire'
        },
        eventName: 'Form ready'
      }
    })
  }

  // update step n form submission
  const handleSubmitSuccess = () => {
    // console.log('Form submitted successfully!')
    captureEvents({
      posthog: {
        data: {
          source: 'pre-demo-questionnaire'
        },
        eventName: 'Form submitted'
      }
    })

    setStep(2)
  }

  const handleSubmitError = () => {
    captureEvents({
      posthog: {
        data: {
          source: 'pre-demo-questionnaire',
          error: true
        },
        eventName: 'Error Event'
      }
    })
    setStep(3) // Add new error state
  }

  return (
    <HeroWrapper>
      <HeroContentWrapper>
        {/* <FadeBoxes /> */}
        <TopHat marginBottom='1rem' color={BrandColors.white} align='center'>
          Thanks for requesting a Flatfile demo
        </TopHat>
        <TitleH1
          marginBottom='2rem'
          fontSize={3.25}
          fontWeight={400}
          color={BrandColors.white}
          className='text-center'
        >
          We just need a little more info from you
        </TitleH1>

        <FormWrapper className='text-white max-w-screen-lg'>
          {/* if its step 1 shwo the basin forms */}
          {step === 1 && (
            <>
              <BasinFormEmbed
                id='questionnaireForm'
                src='https://usebasin.com/form/304a198c127a/view/e9ebaaff694f?iframe=true'
              />
              <BasinEventListener
                // formId='questionnaireForm'
                onFormReady={handleFormReady}
                onSubmitSuccess={handleSubmitSuccess}
                onSubmitError={handleSubmitError}
              />
            </>
          )}

          {/* if its step 2 show a success message */}
          {step === 2 && (
            <div className='submitted-message'>
              <p>
                <span>Thank you for your submission!</span> Someone from our
                team will be in touch shortly.
              </p>
            </div>
          )}

          {step === 3 && (
            <div className='submitted-message error'>
              {/* tailwind info box with error */}
              <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative'>
                <strong className='font-bold'>Error:</strong>
                <span className='block sm:inline'>
                  There was an error submitting the form. Please try again.
                </span>
              </div>
            </div>
          )}
        </FormWrapper>
      </HeroContentWrapper>
    </HeroWrapper>
  )
}

const DemoQuestionnairePage = () => {
  useEffect(() => {
    // Add class to the html element when the page mounts
    document.documentElement.classList.add('fall-release')

    // Clean up by removing the class when the page unmounts
    return () => {
      document.documentElement.classList.remove('fall-release')
    }
  }, [])
  return (
    <Layout>
      <ReleaseBG />
      <LandingPageNav />

      <HeroSection />

      <Footer variant='secondary' showCta={false} />
    </Layout>
  )
}

export default DemoQuestionnairePage

export const Head = ({ data, pageContext }) => {
  return (
    <SEO
      description={'We just need a little more info from you'}
      title={'Thanks for requesting a Flatfile demo'}
    />
  )
}
